import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Check, Minus, Plus, ChevronRight } from 'lucide-react';
import { getProducts } from '../data/productStore';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';

const ProductPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const products = getProducts();

  const product = products.find(p => p.slug === slug);

  if (!product) {
    return (
      <div className="bg-[#0a0a0a] min-h-screen pt-28 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl text-white font-bold mb-4">Product Not Found</h2>
          <Link to="/shop" className="text-[#2EC866] hover:underline">Back to Shop</Link>
        </div>
      </div>
    );
  }

  const relatedProducts = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 3);

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAdded(true);
    toast({ title: 'Added to cart', description: `${product.name} x${quantity}` });
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="bg-[#0a0a0a] min-h-screen pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/shop" className="hover:text-white transition-colors">Shop</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/shop?category=${product.category}`} className="hover:text-white transition-colors">{product.categoryName}</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-400 truncate max-w-[200px]">{product.name}</span>
        </nav>

        {/* Product Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          {/* Image */}
          <div className="bg-[#111] rounded-2xl overflow-hidden border border-gray-800/50">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-[500px] object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>

          {/* Info */}
          <div className="flex flex-col justify-center">
            <span className="text-[#2EC866] text-sm font-medium uppercase tracking-wider mb-3">{product.categoryName}</span>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              {product.name}
            </h1>
            <p className="text-3xl font-bold text-white mb-6">&pound;{product.price.toFixed(2)}</p>
            <p className="text-gray-400 leading-relaxed mb-8">{product.description}</p>

            {/* Features */}
            {product.features && (
              <div className="mb-8">
                <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Features</h3>
                <ul className="space-y-3">
                  {product.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-3 text-gray-400 text-sm">
                      <Check className="w-4 h-4 text-[#2EC866] flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Quantity & Add to Cart */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center bg-[#111] border border-gray-800 rounded-lg">
                <button
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="px-3 py-3 text-gray-400 hover:text-white transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-4 py-3 text-white font-medium min-w-[48px] text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(q => q + 1)}
                  className="px-3 py-3 text-gray-400 hover:text-white transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={handleAddToCart}
                className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-lg font-semibold text-white transition-all duration-300 ${
                  added
                    ? 'bg-green-600'
                    : 'bg-[#2EC866] hover:bg-[#25a855] hover:shadow-[0_0_20px_rgba(46,200,102,0.2)]'
                }`}
              >
                {added ? (
                  <><Check className="w-5 h-5" /> Added to Cart</>
                ) : (
                  <><ShoppingCart className="w-5 h-5" /> Add to Cart</>
                )}
              </button>
            </div>

            <p className="text-gray-600 text-xs">
              {product.inStock ? 'In Stock • Ships within 2-3 business days' : 'Out of Stock'}
            </p>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
              You May Also Like
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProducts.map(rp => (
                <Link
                  key={rp.id}
                  to={`/product/${rp.slug}`}
                  className="group bg-[#111] border border-gray-800/50 rounded-xl overflow-hidden hover:border-[#2EC866]/20 transition-all duration-500"
                >
                  <div className="relative overflow-hidden">
                    <img src={rp.image} alt={rp.name}
                      className="w-full h-48 object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-5">
                    <span className="text-[#2EC866] text-xs font-medium uppercase tracking-wider">{rp.categoryName}</span>
                    <h3 className="text-white font-semibold mt-2 mb-2 text-sm group-hover:text-[#2EC866] transition-colors line-clamp-2">{rp.name}</h3>
                    <p className="text-white font-bold">&pound;{rp.price.toFixed(2)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductPage;
